import React, { useEffect, useState, useRef } from "react"
import { Box, Typography, Paper, Grid, TextField, Button, Divider } from "@mui/material"
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar, Pie } from "react-chartjs-2"
import { useApiService } from "../hooks/Api/useApiService"
import T from "../hooks/Language/T"
import useTranslationSharie from "../hooks/Language/useTranslation"

// Register ChartJS components
Chart.register(BarController, BarElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend)

const DemandAnalyticsPage = () => {
  const { getData } = useApiService()
  const [demands, setDemands] = useState([])
  const [filteredDemands, setFilteredDemands] = useState([])
  const [search, setSearch] = useState("")
  const [stats, setStats] = useState({
    totalDemands: 0,
    avgLikes: 0,
    avgViews: 0,
    topDemands: [],
  })

  const barRef = useRef(null)
  const pieRef = useRef(null)
  const t = useTranslationSharie()
  // Chart data based on demand text with total engagement (likes + views)
  const timeBarData = {
    labels: filteredDemands.slice(0, 10).map((d) => d.demand_text), // full demand text as X labels
    datasets: [
      {
        label: t("Engagement"),
        data: filteredDemands.slice(0, 10).map((d) => d.likes_count + d.views_count),
        backgroundColor: "#4caf50",
      },
    ],
  }

  // Chart options
  const timeBarOptions = {
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const demand = filteredDemands[context.dataIndex]
            if (!demand) return ""
            const date = new Date(demand.created_at).toLocaleString()
            const total = demand.likes_count + demand.views_count
            return [`📌 ${demand.demand_text}`, `📅 ${date}`, `👍+👀 ${total}`]
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: function (val, index) {
            // shorten for display, full text is in tooltip
            const demand = filteredDemands[index]
            return demand ? demand.demand_text.slice(0, 15) + "…" : ""
          },
        },
      },
      y: {
        title: {
          display: false,
          text: "",
        },
      },
    },
  }
  const fetchDemands = async () => {
    try {
      const data = await getData("sharie/getdemands")
      setDemands(data.demands)
      setFilteredDemands(data.demands)
      calculateStats(data.demands)
    } catch (err) {
      console.error(err)
    }
  }

  const calculateStats = (demands) => {
    const totalDemands = demands.length
    const avgLikes =
      totalDemands === 0 ? 0 : demands.reduce((a, b) => a + b.likes_count, 0) / totalDemands
    const avgViews =
      totalDemands === 0 ? 0 : demands.reduce((a, b) => a + b.views_count, 0) / totalDemands
    const topDemands = [...demands].sort((a, b) => b.likes_count - a.likes_count).slice(0, 3)
    setStats({ totalDemands, avgLikes, avgViews, topDemands })
  }

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase()
    setSearch(e.target.value)
    const filtered = demands.filter(
      (d) =>
        d.user_name.toLowerCase().includes(value) || d.demand_text.toLowerCase().includes(value)
    )
    setFilteredDemands(filtered)
    calculateStats(filtered)
  }

  // Chart data
  const barData = {
    labels: filteredDemands.slice(0, 10).map((d) => d.demand_text.slice(0, 15) + "..."),
    datasets: [
      {
        label: t("Likes"),
        data: filteredDemands.slice(0, 10).map((d) => d.likes_count),
        backgroundColor: "#1976d2",
      },
      {
        label: t("Views"),
        data: filteredDemands.slice(0, 10).map((d) => d.views_count),
        backgroundColor: "#64b5f6",
      },
    ],
  }

  const pieData = {
    labels: Array.from(new Set(filteredDemands.map((d) => d.user_name))),
    datasets: [
      {
        data: Array.from(
          filteredDemands.reduce(
            (map, d) => map.set(d.user_name, (map.get(d.user_name) || 0) + 1),
            new Map()
          )
        ).map(([, count]) => count),
        backgroundColor: ["#1976d2", "#64b5f6", "#80cbc4", "#ffb74d", "#f06292", "#ba68c8"],
      },
    ],
  }

  // Destroy charts before re-rendering (prevents canvas error)
  useEffect(() => {
    return () => {
      if (barRef.current) barRef.current.destroy()
      if (pieRef.current) pieRef.current.destroy()
    }
  }, [])

  useEffect(() => {
    fetchDemands()
  }, [])

  return (
    <T>
      <Box sx={{ p: 3 }}>
        <Typography variant='h4' mb={2}>
          Demand Analytics
        </Typography>

        {/* Search */}
        <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
          <TextField
            label={t("Search by user or text")}
            value={search}
            onChange={handleSearch}
            fullWidth
          />
          <Button variant='contained' onClick={() => handleSearch({ target: { value: "" } })}>
            Clear
          </Button>
        </Box>

        {/* Stats Panel */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: "center", background: "#1976d2", color: "white" }}>
              <Typography variant='h6'>Total Demands</Typography>
              <Typography variant='h4'>{stats?.totalDemands}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: "center", background: "#64b5f6", color: "white" }}>
              <Typography variant='h6'>Average Likes</Typography>
              <Typography variant='h4'>{stats?.avgLikes?.toFixed(1)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: "center", background: "#80cbc4", color: "white" }}>
              <Typography variant='h6'>Average Views</Typography>
              <Typography variant='h4'>{stats?.avgViews?.toFixed(1)}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Charts */}
        <div style={{ overflow: "hidden" }}>
          <Grid container spacing={2}>
            {/* Left column: Pie */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Typography variant='h6' mb={2}>
                  Demands by User
                </Typography>
                <Pie ref={pieRef} data={pieData} />
              </Paper>
            </Grid>

            {/* Right column: two bar charts stacked */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2} direction='column'>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant='h6' mb={2}>
                      Top Demands (Likes / Views)
                    </Typography>
                    <Bar ref={barRef} data={barData} />
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant='h6' mb={2}>
                      Demands Timeline
                    </Typography>
                    <Bar data={timeBarData} options={timeBarOptions} />
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Box>
    </T>
  )
}

export default DemandAnalyticsPage
